import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

/**
 * 主題（亮版 / 暗版）
 * ═══════════════════════════════════════════════════════════════
 *
 * 三種狀態，不是兩種：
 *   "dark"   使用者明確選了暗版
 *   "light"  使用者明確選了亮版
 *   "system" 跟著作業系統走（預設）
 *
 * 為什麼要有 system：多數人會把手機設成「日落自動切換」，
 * 只給亮/暗二選一等於強迫他們手動跟著切。
 *
 * 實作細節：
 *  - CSS 的預設（:root）是暗版，.light class 覆寫成亮版
 *  - class 掛在 <html> 上，不是 <body> —— 這樣連捲軸配色都會跟著變
 *  - 首次載入的判斷在 index.html 的 inline script 就做完了（見那裡的說明），
 *    這個 Provider 只負責之後的切換與記憶
 */

export type ThemeChoice = "light" | "dark" | "system";

const STORAGE_KEY = "colouredev.theme";

interface ThemeValue {
  /** 使用者的選擇 */
  choice: ThemeChoice;
  /** 實際套用的主題（system 會被解析成 light 或 dark） */
  resolved: "light" | "dark";
  setChoice: (next: ThemeChoice) => void;
  /** 在亮/暗之間切換（跳過 system，給單鍵切換按鈕用） */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function systemPrefersLight(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: light)").matches;
}

function readStored(): ThemeChoice {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
}

/** 把主題實際套到 <html> 上 */
function apply(resolved: "light" | "dark") {
  const root = document.documentElement;
  root.classList.toggle("light", resolved === "light");
  root.classList.toggle("dark", resolved === "dark");
  // color-scheme 讓瀏覽器原生元件（下拉選單、日期選擇器）也跟著變
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored);
  const [systemLight, setSystemLight] = useState(systemPrefersLight);

  // 選 system 時要跟著作業系統即時變動（例如日落自動切換）
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => setSystemLight(e.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const resolved: "light" | "dark" =
    choice === "system" ? (systemLight ? "light" : "dark") : choice;

  useEffect(() => {
    apply(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo<ThemeValue>(
    () => ({
      choice,
      resolved,
      setChoice,
      toggle: () => setChoice(resolved === "dark" ? "light" : "dark"),
    }),
    [choice, resolved, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
